import { useEffect, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Eye, Flame, Hand, Heart, MapPin, Sparkles, ThumbsUp, X } from "lucide-react";
import { versionMedia } from "@/lib/apercu";

const ROUGE = "#FF4757";

export const REACTIONS_LOVE = [
  { cle: "main", label: "Coucou", Icone: Hand, couleur: "#78716C" },
  { cle: "like", label: "J'aime", Icone: ThumbsUp, couleur: "#2563EB" },
  { cle: "coeur", label: "Coup de cœur", Icone: Heart, couleur: ROUGE },
  { cle: "feu", label: "Ça chauffe", Icone: Flame, couleur: "#EA580C" },
];

export const photoLove = (profil, moiId) =>
  `${process.env.REACT_APP_BACKEND_URL}${profil.photo}?v=${versionMedia()}${
    moiId ? `&viewer_id=${moiId}` : ""
  }`;

/** Pile de cartes Love : glisser à droite = coup de cœur, à gauche = passer.
 *  Les 4 réactions restent accessibles en boutons sous la carte. */
export const LoveSwipe = ({ profils, moiId, onReagir, onPasser, onEpuise }) => {
  const [index, setIndex] = useState(0);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-220, 220], [-14, 14]);
  const opaciteCoeur = useTransform(x, [40, 150], [0, 1]);
  const opacitePasse = useTransform(x, [-150, -40], [1, 0]);

  // La pile ne revient au début que si la LISTE change réellement : un simple
  // rechargement identique (ou une nouvelle référence de tableau) ne doit jamais
  // faire sauter la carte en cours vers un autre profil.
  const signature = profils.map((p) => p.id).join("|");
  useEffect(() => { setIndex(0); }, [signature]);
  useEffect(() => {
    if (profils.length && index >= profils.length) onEpuise?.();
  }, [index, profils.length]);

  const profil = profils[index];
  const suivant = profils[index + 1];

  const avancer = () => { x.set(0); setIndex((i) => i + 1); };

  const reagir = (type) => { onReagir(profil, type); avancer(); };
  const passer = () => { onPasser(profil); avancer(); };

  const finDragEnd = (_, info) => {
    if (info.offset.x > 120) reagir("coeur");
    else if (info.offset.x < -120) passer();
    else x.set(0);
  };

  if (!profil) {
    return (
      <div className="mt-10 rounded-3xl bg-white p-10 text-center shadow-sm ring-1 ring-stone-900/5"
        data-testid="love-swipe-epuise">
        <p className="font-heading text-xl font-extrabold text-[#2F363F]">
          Vous avez vu tous les profils du moment
        </p>
        <p className="mt-2 text-sm text-stone-500">
          Revenez un peu plus tard : de nouveaux membres s'inscrivent chaque jour.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 flex flex-col items-center" data-testid="love-swipe">
      <div className="relative h-[520px] w-full max-w-sm">
        {suivant && (
          <div className="absolute inset-0 scale-[0.94] overflow-hidden rounded-[2rem] bg-stone-200 opacity-60 shadow-sm">
            <img src={photoLove(suivant, moiId)} alt="" aria-hidden
              className="h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
        )}

        <motion.article
          key={profil.id}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.7}
          style={{ x, rotate }}
          onDragEnd={finDragEnd}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid={`love-swipe-carte-${profil.id}`}
          className="absolute inset-0 cursor-grab overflow-hidden rounded-[2rem] bg-stone-900 shadow-2xl active:cursor-grabbing"
        >
          <span className="absolute inset-0 flex items-center justify-center font-heading text-7xl font-extrabold text-white/20">
            {(profil.firstname || "?").charAt(0)}
          </span>
          <img src={photoLove(profil, moiId)} alt={profil.firstname} draggable={false}
            className="h-full w-full select-none object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />

          <motion.span style={{ opacity: opaciteCoeur }}
            className="pointer-events-none absolute left-5 top-6 rounded-xl border-4 border-[#FF4757] px-4 py-1.5 font-heading text-2xl font-extrabold uppercase text-[#FF4757]">
            Coup de cœur
          </motion.span>
          <motion.span style={{ opacity: opacitePasse }}
            className="pointer-events-none absolute right-5 top-6 rounded-xl border-4 border-white px-4 py-1.5 font-heading text-2xl font-extrabold uppercase text-white">
            Passer
          </motion.span>

          {profil.photo_visibilite !== "visible" && (
            <span data-testid={`love-anonymat-${profil.id}`}
              className="absolute right-4 top-20 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
              {profil.photo_visibilite === "anonyme" ? "Anonyme" : "Photo floutée"}
            </span>
          )}

          <div className="absolute inset-x-0 bottom-0 space-y-2 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6">
            <h3 className="font-heading text-3xl font-extrabold text-white">
              {profil.firstname}{profil.age ? `, ${profil.age}` : ""}
            </h3>
            <p className="inline-flex items-center gap-1.5 text-xs text-white/80">
              <MapPin size={12} /> {profil.quartier || profil.commune || profil.city || "Côte d'Ivoire"}
              {profil.taille ? ` · ${profil.taille}` : ""}
              {profil.silhouette ? ` · ${profil.silhouette}` : ""}
            </p>
            {profil.recherche && (
              <p className="inline-flex items-center gap-1.5 rounded-full bg-[#FF4757] px-3 py-1 text-[11px] font-bold text-white">
                <Sparkles size={11} /> {profil.recherche}
              </p>
            )}
            {profil.bio && <p className="text-sm leading-relaxed text-white/85 line-clamp-2">{profil.bio}</p>}
            <p className="inline-flex items-center gap-1 text-[11px] font-semibold text-white/60">
              <Eye size={11} /> {profil.vues} vues · <Heart size={11} /> {profil.affinites} affinités
            </p>
          </div>
        </motion.article>
      </div>

      <div className="mt-6 flex w-full max-w-sm items-center justify-between gap-2">
        <button onClick={passer} title="Passer" data-testid={`love-passer-${profil.id}`}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-stone-400 shadow-sm ring-1 ring-stone-900/5 transition-transform hover:scale-110 hover:text-stone-700">
          <X size={24} />
        </button>
        {REACTIONS_LOVE.map(({ cle, label, Icone, couleur }) => (
          <button key={cle} title={label} onClick={() => reagir(cle)}
            data-testid={`love-reaction-${cle}-${profil.id}`}
            className="flex flex-1 flex-col items-center gap-1 rounded-2xl bg-white py-3 shadow-sm ring-1 ring-stone-900/5 transition-transform hover:scale-105"
            style={{ color: couleur }}>
            <Icone size={22} />
            <span className="text-[10px] font-bold text-stone-500">{label}</span>
          </button>
        ))}
      </div>
      <p className="mt-4 text-center text-xs text-stone-400">
        Glissez la carte à droite pour un coup de cœur, à gauche pour passer.
      </p>
    </div>
  );
};
